import type { ReactNode } from "react";
import { Sparkline, type SparklineSeries } from "components/charts";
import { Text } from "components/text";
import { Skeleton } from "components/skeleton";

export const SummaryCard = ({
  testId,
  icon,
  title,
  isLoading,
  badge,
  body,
  series = [],
  unavailableReason,
  detailSummary,
  detailValue,
  jumpHint,
  onClick,
}: {
  testId: string;
  icon: ReactNode;
  title: string;
  isLoading?: boolean;
  badge: string | null;
  body?: ReactNode;
  series?: SparklineSeries[];
  unavailableReason?: string;
  detailSummary?: string;
  detailValue?: string;
  jumpHint: string;
  onClick: () => void;
}) => {
  if (isLoading) {
    return <Skeleton className="h-[226px] w-full rounded-xl" />;
  }

  return (
    <button
      type="button"
      data-testid={testId}
      className="group relative flex w-full cursor-pointer flex-col gap-3 rounded-xl border border-border bg-background p-4 text-left transition-colors hover:border-primary-500 focus-visible:border-primary-500 focus-visible:outline-none"
      onClick={onClick}
      aria-label={`${title}, ${jumpHint}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Text as="span" variant="metric">
            {title}
          </Text>
          {badge && (
            <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5">
              <Text as="span" variant="metricCaption" className="uppercase">
                {badge}
              </Text>
            </span>
          )}
        </div>
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors group-hover:bg-primary-50 group-hover:text-primary-500"
          aria-hidden
        >
          {icon}
        </span>
      </header>

      {unavailableReason ? (
        <Text as="span" variant="metric">
          {unavailableReason}
        </Text>
      ) : (
        <>
          {body}
          <Sparkline series={series} />
        </>
      )}

      {detailSummary && (
        <footer className="mt-auto flex items-center justify-between gap-2 border-t border-border pt-2">
          <Text as="span" variant="metricCaption">
            {detailSummary}
          </Text>
          {detailValue != null && (
            <Text
              as="span"
              variant="bodyStrong"
              numeric="tabular"
              className="truncate text-right"
            >
              {detailValue}
            </Text>
          )}
        </footer>
      )}
    </button>
  );
};
